import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Facebook, 
  Instagram, 
  Twitter, 
  Linkedin, 
  Youtube, 
  Globe,
  Save,
  Eye,
  ExternalLink,
  Plus,
  Trash2,
  Edit
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import AdminLayout from '../../components/admin/AdminLayout'

const SocialMediaManagement = () => {
  const { socialMediaLinks, updateSocialMediaLinks } = useStore()
  const [editingPlatform, setEditingPlatform] = useState(null)
  const [formData, setFormData] = useState({
    platform: '',
    url: '',
    isActive: true
  })

  const platforms = [
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-pink-600' },
    { id: 'twitter', name: 'Twitter', icon: Twitter, color: 'bg-blue-400' },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-blue-700' },
    { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'bg-red-600' },
    { id: 'website', name: 'Site Web', icon: Globe, color: 'bg-gray-600' }
  ]

  const handleEdit = (platform) => {
    const existingLink = socialMediaLinks.find(link => link.platform === platform.id)
    setFormData({
      platform: platform.id,
      url: existingLink?.url || '',
      isActive: existingLink?.isActive ?? true
    })
    setEditingPlatform(platform)
  }

  const handleSave = () => {
    const updatedLinks = socialMediaLinks.filter(link => link.platform !== formData.platform)
    
    if (formData.url.trim()) {
      updatedLinks.push({
        platform: formData.platform,
        url: formData.url.trim(),
        isActive: formData.isActive
      })
    }
    
    updateSocialMediaLinks(updatedLinks)
    setEditingPlatform(null)
    setFormData({ platform: '', url: '', isActive: true })
  }

  const handleDelete = (platformId) => {
    const updatedLinks = socialMediaLinks.filter(link => link.platform !== platformId)
    updateSocialMediaLinks(updatedLinks)
  }

  const getPlatformInfo = (platformId) => {
    return platforms.find(p => p.id === platformId)
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* En-tête */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gestion des Réseaux Sociaux
            </h1>
            <p className="text-gray-600">
              Personnalisez les liens vers vos réseaux sociaux qui apparaissent sur le site web.
            </p>
          </div>

          {/* Liste des plateformes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {platforms.map((platform) => {
              const existingLink = socialMediaLinks.find(link => link.platform === platform.id)
              const Icon = platform.icon
              
              return (
                <motion.div
                  key={platform.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: platforms.indexOf(platform) * 0.1 }}
                >
                  <GlassCard className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${platform.color} text-white`}>
                          <Icon size={20} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{platform.name}</h3>
                          <p className="text-sm text-gray-500">
                            {existingLink ? 'Configuré' : 'Non configuré'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {existingLink && (
                          <button
                            onClick={() => window.open(existingLink.url, '_blank')}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Voir le lien"
                          >
                            <ExternalLink size={16} />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleEdit(platform)}
                          className="p-1 text-blue-600 hover:text-blue-700 transition-colors"
                          title="Modifier"
                        >
                          <Edit size={16} />
                        </button>
                        
                        {existingLink && (
                          <button
                            onClick={() => handleDelete(platform.id)}
                            className="p-1 text-red-600 hover:text-red-700 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    {existingLink && (
                      <div className="text-sm text-gray-600 truncate">
                        {existingLink.url}
                      </div>
                    )}
                  </GlassCard>
                </motion.div>
              )
            })}
          </div>

          {/* Formulaire d'édition */}
          {editingPlatform && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <GlassCard className="p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className={`p-2 rounded-lg ${editingPlatform.color} text-white`}>
                    <editingPlatform.icon size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Configurer {editingPlatform.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Entrez l'URL de votre profil {editingPlatform.name}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <Input
                    label="URL du profil"
                    placeholder={`https://${editingPlatform.id}.com/votre-profil`}
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  />
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="isActive" className="text-sm text-gray-700">
                      Afficher sur le site web
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                  <Button
                    onClick={() => setEditingPlatform(null)}
                    variant="outline"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Save size={16} className="mr-2" />
                    Sauvegarder
                  </Button>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Aperçu */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Eye size={20} className="mr-2" />
                Aperçu des Boutons Sociaux
              </h3>
              
              <div className="flex items-center space-x-3">
                {socialMediaLinks
                  .filter(link => link.isActive)
                  .map((link) => {
                    const platform = getPlatformInfo(link.platform)
                    if (!platform) return null
                    
                    const Icon = platform.icon
                    return (
                      <button
                        key={link.platform}
                        onClick={() => window.open(link.url, '_blank')}
                        className={`p-3 rounded-lg ${platform.color} text-white hover:opacity-80 transition-opacity`}
                        title={`Visiter ${platform.name}`}
                      >
                        <Icon size={20} />
                      </button>
                    )
                  })}
                
                {socialMediaLinks.filter(link => link.isActive).length === 0 && (
                  <p className="text-gray-500 text-sm">
                    Aucun réseau social configuré
                  </p>
                )}
              </div>
            </GlassCard>
          </motion.div>
        </motion.div>
      </div>
    </AdminLayout>
  )
}

export default SocialMediaManagement
